import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth'
import { api, ApiError } from '../api'
import { useI18n } from '../i18n'
import type { Booking, DayResponse, HourCell } from '../types'
import { formatHour, formatHourRange, formatLong } from '../lib/dates'
import { RAG_META } from '../lib/rag'
import { Avatar, CoverageBar, Empty, Modal, RoleChip, Spinner, useConfirm, useToast } from './ui'
import { IconCheck, IconChevronLeft, IconX } from './icons'

interface HourRole {
  id: string
  name: string
  color?: string | null
}

export default function DayView({
  date,
  onBack,
  onChanged,
}: {
  date: string
  onBack?: () => void
  onChanged?: () => void
}) {
  const { hasRole } = useAuth()
  const { t } = useI18n()
  const toast = useToast()
  const confirm = useConfirm()
  const isSupervisor = hasRole('supervisor')
  const [data, setData] = useState<DayResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [showFull, setShowFull] = useState(false)
  const [actingId, setActingId] = useState<string | null>(null)
  // The hour whose role chooser is open (null = no chooser shown).
  const [roleChooserHour, setRoleChooserHour] = useState<number | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await api.day(date)
      setData(res)
    } catch {
      toast.push(t('toast.loadDayErr'), 'error')
    } finally {
      setLoading(false)
    }
  }, [date, toast])

  useEffect(() => {
    setLoading(true)
    load()
  }, [load])

  const myByHour = useMemo(() => {
    const map = new Map<number, Booking>()
    if (data) for (const b of data.myBookings) if (b.status !== 'cancelled') map.set(b.hour, b)
    return map
  }, [data])

  const gridByHour = useMemo(() => {
    const map = new Map<number, HourCell>()
    if (data) for (const c of data.grid) map.set(c.hour, c)
    return map
  }, [data])

  // Co-workers sharing the viewer's booked hours, grouped by hour.
  const coworkersByHour = useMemo(() => {
    const map = new Map<number, Booking[]>()
    if (data?.coworkers) {
      for (const b of data.coworkers) {
        const list = map.get(b.hour)
        if (list) list.push(b)
        else map.set(b.hour, [b])
      }
    }
    return map
  }, [data])

  const [lo, hi] = useMemo(() => {
    if (!data || showFull) return [0, 23]
    let l = 8
    let h = 19
    for (const d of data.demands) {
      l = Math.min(l, d.start_hour)
      h = Math.max(h, d.end_hour - 1)
    }
    for (const b of data.bookings) {
      l = Math.min(l, b.hour)
      h = Math.max(h, b.hour)
    }
    for (const b of data.myBookings) {
      l = Math.min(l, b.hour)
      h = Math.max(h, b.hour)
    }
    return [Math.max(0, l), Math.min(23, h)]
  }, [data, showFull])

  // The distinct job roles asked for in the windows covering this hour.
  function rolesForHour(hour: number): HourRole[] {
    const seen = new Map<string, HourRole>()
    if (data) {
      for (const d of data.demands) {
        if (d.start_hour <= hour && d.end_hour > hour && d.role_id && !seen.has(d.role_id)) {
          seen.set(d.role_id, { id: d.role_id, name: d.role_name ?? '', color: d.role_color })
        }
      }
    }
    return [...seen.values()]
  }

  // Open the role chooser if this hour needs a role; otherwise book directly.
  function startBooking(hour: number) {
    if (rolesForHour(hour).length >= 1) setRoleChooserHour(hour)
    else book(hour)
  }

  async function book(hour: number, roleId?: string) {
    setActingId(`book-${hour}`)
    try {
      await api.createBooking({ date, hour, role_id: roleId ?? null })
      toast.push(t('toast.booked'), 'ok')
      await load()
      onChanged?.()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.bookErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  async function cancelMine(b: Booking) {
    if (!(await confirm())) return
    setActingId(`cancel-${b.id}`)
    try {
      if (b.status === 'pending') await api.deleteBooking(b.id)
      else await api.updateBooking(b.id, 'cancelled')
      toast.push(t('toast.cancelled'), 'ok')
      await load()
      onChanged?.()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.cancelErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  async function decide(b: Booking, status: 'approved' | 'rejected') {
    // Rejecting / removing permanently discards the booking — confirm first.
    if (status === 'rejected' && !(await confirm())) return
    setActingId(`${status}-${b.id}`)
    try {
      await api.updateBooking(b.id, status)
      toast.push(status === 'approved' ? t('toast.approved') : t('toast.rejected'), 'ok')
      await load()
      onChanged?.()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.updateErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  async function revert(b: Booking) {
    setActingId(`revert-${b.id}`)
    try {
      await api.revertBooking(b.id)
      toast.push(t('toast.reverted'), 'ok')
      await load()
      onChanged?.()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.updateErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  async function offer(b: Booking, on: boolean) {
    setActingId(`offer-${b.id}`)
    try {
      if (on) await api.offerBooking(b.id)
      else await api.unofferBooking(b.id)
      toast.push(on ? t('toast.offered') : t('toast.withdrawn'), 'ok')
      await load()
      onChanged?.()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.offerErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  async function take(b: Booking) {
    setActingId(`take-${b.id}`)
    try {
      await api.takeBooking(b.id)
      toast.push(t('toast.taken'), 'ok')
      await load()
      onChanged?.()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.takeErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  if (loading) {
    return (
      <div className="card" style={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
        <Spinner large />
      </div>
    )
  }
  if (!data) return null

  const meta = RAG_META[data.summary.rag]
  const pending = isSupervisor ? data.bookings.filter((b) => b.status === 'pending') : []
  const approved = isSupervisor ? data.bookings.filter((b) => b.status === 'approved') : []

  // "What's on": demands carrying an event name or a job role.
  const whatsOn = data.demands.filter((d) => d.title || d.role_name)

  const hours: number[] = []
  for (let h = lo; h <= hi; h++) hours.push(h)

  return (
    <div className="col gap-16">
      <div className="card">
        <div className="between wrap">
          <div className="row">
            {onBack && (
              <button className="iconbtn" onClick={onBack} aria-label={t('common.back')}>
                <IconChevronLeft />
              </button>
            )}
            <div>
              <h2>{formatLong(date)}</h2>
              <div className="row gap-8 mt-8">
                <span className="badge" style={{ background: meta.soft, color: meta.ink }}>
                  <span className={`rag-dot ${data.summary.rag}`} /> {t('rag.' + data.summary.rag)}
                </span>
                {data.summary.open ? (
                  <span className="muted" style={{ fontSize: 13 }}>
                    {t('day.personHours', { covered: data.summary.covered, required: data.summary.required })}
                    {data.summary.pending > 0 ? ` · ${t('cal.pending', { n: data.summary.pending })}` : ''}
                  </span>
                ) : (
                  <span className="muted" style={{ fontSize: 13 }}>
                    {t('day.noDemand')}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowFull((v) => !v)}>
            {showFull ? t('day.workingHours') : t('day.fullDay')}
          </button>
        </div>

        {whatsOn.length > 0 && (
          <div className="mt-16">
            <div className="muted" style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
              {t('day.whatsOn')}
            </div>
            <div className="list">
              {whatsOn.map((d) => (
                <div className="list-row" key={d.id}>
                  <div className="grow">
                    <div className="title" style={{ fontWeight: 800 }}>
                      {d.title || d.role || t('ot.noLabel')}
                    </div>
                    <div className="sub">{formatHourRange(d.start_hour, d.end_hour)}</div>
                  </div>
                  {d.role_name && <RoleChip name={d.role_name} color={d.role_color} />}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16">
          {hours.map((hour) => {
            const cell = gridByHour.get(hour) ?? { hour, required: 0, approved: 0, pending: 0 }
            const mine = myByHour.get(hour)
            const open = cell.required > 0
            const rowCls = `hour-row ${mine ? 'mine' : open ? 'open' : ''}`
            const pct = open ? Math.round((Math.min(cell.approved, cell.required) / cell.required) * 100) : 0
            const rag = !open ? 'none' : pct >= 100 ? 'green' : pct > 0 ? 'amber' : 'red'
            const mates = mine ? coworkersByHour.get(hour) ?? [] : []
            return (
              <div key={hour}>
              <div className={rowCls}>
                <div className="hour-time">{formatHour(hour)}</div>
                <div className="hour-meta">
                  {open ? (
                    <>
                      <div className="hour-need">
                        {t('day.filled', { approved: cell.approved, required: cell.required })}
                        {cell.pending > 0 ? ` · ${t('cal.pending', { n: cell.pending })}` : ''}
                      </div>
                      <div style={{ maxWidth: 220 }}>
                        <CoverageBar rag={rag} pct={pct} />
                      </div>
                    </>
                  ) : (
                    <div className="hour-sub">
                      {cell.approved + cell.pending > 0
                        ? `${t('day.booked', { approved: cell.approved })}${cell.pending ? `, ${t('cal.pending', { n: cell.pending })}` : ''}`
                        : t('day.openToBook')}
                    </div>
                  )}
                </div>
                <div className="row gap-8">
                  {mine ? (
                    <>
                      {mine.role_name && <RoleChip name={mine.role_name} color={mine.role_color} />}
                      <span className={`badge ${mine.status === 'approved' ? 'green' : 'amber'}`}>
                        {mine.status === 'approved' ? t('day.youreOn') : t('status.pending')}
                      </span>
                      {mine.status === 'approved' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => offer(mine, !mine.offered_at)}
                          disabled={actingId === `offer-${mine.id}`}
                        >
                          {actingId === `offer-${mine.id}` ? (
                            <Spinner />
                          ) : mine.offered_at ? (
                            t('day.withdraw')
                          ) : (
                            t('day.offer')
                          )}
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => cancelMine(mine)}
                        disabled={actingId === `cancel-${mine.id}`}
                      >
                        {actingId === `cancel-${mine.id}` ? <Spinner /> : t('common.cancel')}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => startBooking(hour)}
                      disabled={actingId === `book-${hour}`}
                    >
                      {actingId === `book-${hour}` ? <Spinner /> : t('day.book')}
                    </button>
                  )}
                </div>
              </div>
              {mates.length > 0 && (
                <div className="hour-sub row gap-8" style={{ flexWrap: 'wrap', padding: '4px 0 8px 78px' }}>
                  <span>{t('day.coworkers')}:</span>
                  {mates.map((m, i) => (
                    <span key={m.id} className="row gap-8">
                      <span>
                        {m.user_name || m.user_email}
                        {i < mates.length - 1 ? ',' : ''}
                      </span>
                      {m.role_name && <RoleChip name={m.role_name} color={m.role_color} />}
                    </span>
                  ))}
                </div>
              )}
              </div>
            )
          })}
        </div>
      </div>

      {isSupervisor && (
        <div className="card">
          <div className="card-head">
            <h3>{t('day.peopleApprovals')}</h3>
            <span className="tag">{t('day.bookings', { n: data.bookings.length })}</span>
          </div>

          {pending.length === 0 && approved.length === 0 ? (
            <Empty title={t('day.noBookings')}>
              <p className="muted">{t('day.noBookingsHint')}</p>
            </Empty>
          ) : (
            <div className="col gap-16">
              {pending.length > 0 && (
                <div>
                  <div className="muted" style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                    {t('day.awaiting')}
                  </div>
                  <div className="list">
                    {pending.map((b) => (
                      <div className="list-row" key={b.id}>
                        <Avatar name={b.user_name} email={b.user_email} sm />
                        <div className="grow">
                          <div className="title">{b.user_name || b.user_email}</div>
                          <div className="sub">{formatHour(b.hour)}</div>
                        </div>
                        {b.role_name && <RoleChip name={b.role_name} color={b.role_color} />}
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => decide(b, 'approved')}
                          disabled={actingId === `approved-${b.id}`}
                        >
                          <IconCheck width={16} height={16} /> {t('day.approve')}
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => decide(b, 'rejected')}
                          disabled={actingId === `rejected-${b.id}`}
                        >
                          <IconX width={16} height={16} /> {t('day.reject')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {approved.length > 0 && (
                <div>
                  <div className="muted" style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                    {t('day.confirmed')}
                  </div>
                  <div className="list">
                    {approved.map((b) => (
                      <div className="list-row" key={b.id}>
                        <Avatar name={b.user_name} email={b.user_email} green sm />
                        <div className="grow">
                          <div className="title">{b.user_name || b.user_email}</div>
                          <div className="sub">{formatHour(b.hour)}</div>
                        </div>
                        {b.role_name && <RoleChip name={b.role_name} color={b.role_color} />}
                        <span className="badge green">{t('status.approved')}</span>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => revert(b)}
                          disabled={actingId === `revert-${b.id}`}
                        >
                          {actingId === `revert-${b.id}` ? <Spinner /> : t('day.revert')}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => decide(b, 'rejected')}
                          disabled={actingId === `rejected-${b.id}`}
                        >
                          {t('day.remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {roleChooserHour !== null && (
        <Modal title={t('day.chooseRole')} onClose={() => setRoleChooserHour(null)}>
          <div className="col gap-8" style={{ marginTop: 12 }}>
            {rolesForHour(roleChooserHour).map((r) => (
              <button
                key={r.id}
                type="button"
                className="btn btn-ghost"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => {
                  const hour = roleChooserHour
                  setRoleChooserHour(null)
                  book(hour, r.id)
                }}
              >
                <RoleChip name={r.name} color={r.color} />
              </button>
            ))}
            <button
              type="button"
              className="btn btn-ghost"
              style={{ justifyContent: 'flex-start' }}
              onClick={() => {
                const hour = roleChooserHour
                setRoleChooserHour(null)
                book(hour)
              }}
            >
              {t('day.noRole')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
